import { useApolloClient } from '@apollo/react-hooks';
import {
  addDays,
  differenceInMinutes,
  format,
  isAfter,
  isBefore,
  isValid,
  isWithinInterval,
  parse,
  subHours,
  subMinutes
} from 'date-fns';
import { pipe } from 'lodash/fp';
import { IntlShape } from 'react-intl';

import { ILocation, SupportedRegions } from '@rbi-ctg/frontend';
import { IStore, IStoreHoursOfOperation } from '@rbi-ctg/store';
import {
  DeliveryQuoteDocument,
  DeliveryStatus,
  GetRestaurantDocument,
  IDeliveryQuoteQuery,
  IGetRestaurantQuery,
  IRestaurant
} from 'generated/rbi-graphql';
import { StoreStatus } from 'remote/api/restaurants';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ServiceMode } from 'state/order';
import { StoreProxy } from 'state/store/hooks';
import { StatusType, dataDogLogger } from 'utils/datadog';
import { convertMilesToFeet, convertMilesToKilometers, convertMilesToMeters } from 'utils/distance';
import { IPlaceAddress } from 'utils/geolocation';
import logger from 'utils/logger';

import { validMobileOrderingEnvs } from '../environment';
import { haversine } from '../geometry';

import {
  ALT_PARSE_FORMAT,
  LOCALE_TIME_PARSE_FORMAT,
  MIDNIGHT,
  PARSE_FORMAT,
  TWELVE_HOUR_TIME_PARSE_FORMAT
} from './constants';

export { getUSState } from './get-us-state';
export { default as remapStore } from './remap-store';

export enum SanityTypes {
  ADDRESS = 'address',
  HOURS_OF_OPERATION = 'hoursOfOperation',
  POS = 'pos'
}

export enum Days {
  sun,
  mon,
  tue,
  wed,
  thr,
  fri,
  sat
}

export enum HeartbeatOverrideStatus {
  Online = 'online',
  Offline = 'offline',
  Auto = 'auto'
}

type IRbiRestaurant = IRestaurant | IGetRestaurantQuery['restaurant'];

type ICheckAvailability = (rbiRestaurant?: IRbiRestaurant) => boolean;

type IGetRestaurantFn = (storeId: string) => Promise<IRbiRestaurant>;

interface IMergeRestaurantData {
  rbiRestaurant: IRbiRestaurant;
  sanityStore: IStore;
}

/**
 * Utility hook for determining restaurant availability
 *
 * @returns {ICheckAvailability}
 * * Function that determines availability based on the result of the
 * * GetRestaurant query and the ENABLE_ORDERING LD flag
 * * If the query throws an errors, or the flag is off, then we default to `true`
 * * otherwise we return the `restaurant.available` boolean
 */
export const useGetRestaurantAvailabilityFn = (): ICheckAvailability => {
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);

  return rbiRestaurant => {
    // if `available` is null | undefined, it means the query for RbiRestaurant failed
    // in this case we default to true
    const isAvailable = rbiRestaurant?.available ?? true;

    // if LD is offline for any reason, we default to true
    return enableOrdering ? isAvailable : true;
  };
};

/**
 * Utility hook that provides a function to query an rbiRestaurant by id
 *
 * @returns {IGetRestaurantFn}
 * Function that retrieves an RbiRestaurant with the most up to date data
 * It will pass the restaurant through `ICheckAvailability` before returning
 */
export const useGetRestaurantFn = (): IGetRestaurantFn => {
  const client = useApolloClient();
  const checkAvailability = useGetRestaurantAvailabilityFn();

  const getRestaurantFn = async (storeId: string) => {
    const { data } = await client.query({
      // we always want the most up to date data when calculating heartbeat freshness
      fetchPolicy: 'network-only',
      variables: { storeId },
      query: GetRestaurantDocument
    });

    const rbiRestaurant = data?.restaurant;

    return {
      ...rbiRestaurant,
      available: checkAvailability(rbiRestaurant)
    };
  };
  return getRestaurantFn;
};

/**
 * Merges the data from the GetRestaurant query and sanity query
 *
 * @param {IMergeRestaurantData} data
 * * rbiRestaurant - The restaurant returend from GetRestaurant
 * * sanityStore - the sanity document queried from groq
 *
 * @returns {IStore} merged store document
 *
 *
 * *NOTE* at the moment only the operating hours come from GetRestaurant
 */
export const mergeRestaurantData = ({
  rbiRestaurant,
  sanityStore
}: IMergeRestaurantData): IStore => {
  return {
    ...sanityStore,
    curbsideHours: rbiRestaurant.curbsideHours || sanityStore.curbsideHours,
    deliveryHours: rbiRestaurant.deliveryHours || sanityStore.deliveryHours,
    diningRoomHours: rbiRestaurant.diningRoomHours || sanityStore.diningRoomHours,
    driveThruHours: rbiRestaurant.driveThruHours || sanityStore.driveThruHours
  } as IStore;
};

export function nextDay(now: Date) {
  return Days[(now.getDay() + 1) % 7];
}

export function previousDay(now: Date) {
  return Days[(now.getDay() - 1) % 7];
}

/*
 * Determines what day is the next open day and returns the open hour
 */
export function nextOpenDay(hours: IStoreHoursOfOperation): string {
  const now = new Date();
  return hours[nextDay(now).concat('Open')];
}

/**
 * Parses a time string from mdm
 *
 * time strings can come in three flavors, so we have to try to parse all three
 * * yyyy-MM-dd HH:mm:ss.SSSSSSS
 * * yyyy-MM-dd HH:mm:ss
 * * HH:mm:ss
 *
 */
export function parseMdmTimeString(timeString: string): Date {
  let formattedTime = parse(timeString, PARSE_FORMAT, new Date());

  if (!isValid(formattedTime)) {
    formattedTime = parse(timeString, ALT_PARSE_FORMAT, new Date());
  }

  if (!isValid(formattedTime)) {
    formattedTime = parse(timeString, LOCALE_TIME_PARSE_FORMAT, new Date());
  }

  return formattedTime;
}

export function readableHour(timeString: string): string {
  try {
    return format(parseMdmTimeString(timeString), TWELVE_HOUR_TIME_PARSE_FORMAT);
  } catch (error) {
    return 'hours not available';
  }
}

export const readableTimeInterval = (fallbackMessage: string, open24HoursMessage?: string) => (
  fromTime: string,
  toTime: string
) => {
  try {
    const readableFromHour = format(parseMdmTimeString(fromTime), TWELVE_HOUR_TIME_PARSE_FORMAT);
    const readableToHour = format(parseMdmTimeString(toTime), TWELVE_HOUR_TIME_PARSE_FORMAT);

    // we cannot simply check for equality because we only want to show
    // that a store is open 24 hours if the open/close times are both midnight
    // and not just any 24 hour interval.
    const is24HourInterval = readableFromHour === MIDNIGHT && readableToHour === MIDNIGHT;

    if (is24HourInterval && open24HoursMessage) {
      return open24HoursMessage;
    }

    return `${readableFromHour} - ${readableToHour}`;
  } catch (e) {
    return fallbackMessage;
  }
};

function getTodaysHoursOfOperation(
  hours: IStoreHoursOfOperation,
  now: Date = new Date()
): [string, string] {
  if (!hours) {
    return ['', ''];
  }
  const dayName = Days[now.getDay()];
  const openHours: string = hours[dayName + 'Open'];
  const closeHours: string = hours[dayName + 'Close'];
  return [openHours, closeHours];
}

export function readableCloseHourToday(hours: IStoreHoursOfOperation | null) {
  if (!hours) {
    return 'N/A';
  }

  const [, closeHours] = getTodaysHoursOfOperation(hours);

  if (!closeHours) {
    return 'N/A';
  }

  return readableHour(closeHours);
}

export function readableImperialDistance(
  miles: number,
  milesLocale: string,
  feetLocale: string
): string {
  if (miles >= 10) {
    return `${miles.toFixed(0)} ${milesLocale}`;
  } else if (miles >= 0.1) {
    return `${miles.toFixed(1)} ${milesLocale}`;
  } else {
    return `${convertMilesToFeet(miles).toFixed(0)} ${feetLocale}`;
  }
}

export function readableMetricDistance(
  miles: number,
  kilometersLocale: string,
  metersLocale: string
): string {
  const kilometers = convertMilesToKilometers(miles);
  if (kilometers >= 10) {
    return `${kilometers.toFixed(0)} ${kilometersLocale}`;
  } else if (kilometers >= 0.1) {
    return `${kilometers.toFixed(1)} ${kilometersLocale}`;
  } else {
    return `${convertMilesToMeters(miles).toFixed(0)} ${metersLocale}`;
  }
}

export function readableDistanceFromStore(
  miles: number,
  region: SupportedRegions,
  formatMessage: IntlShape['formatMessage']
): string {
  if (['US', 'GB'].includes(region)) {
    return readableImperialDistance(
      miles,
      formatMessage({ id: 'miles' }),
      formatMessage({ id: 'feet' })
    );
  } else {
    return readableMetricDistance(
      miles,
      formatMessage({ id: 'kilometers' }),
      formatMessage({ id: 'meters' })
    );
  }
}

/*
 * Composable version of Date.getTime()
 */
const dateToTime = (date: Date) => date.getTime();

/*
 * Given a time and a reference date, return a Date that is set to the reference
 * date's month, day, year, and the hours, minutes, seconds from time.
 */
const getDateForTime = (time: number, referenceDate: Date = new Date()): Date => {
  const date = new Date(time);
  date.setFullYear(referenceDate.getFullYear());
  date.setMonth(referenceDate.getMonth());
  date.setDate(referenceDate.getDate());

  return date;
};

/*
 * Given a time string of the format "yyyy-MM-dd HH:mm:ss",
 * return today's date with hours, minutes, and seconds from the time string.
 */
const getDateForHours = pipe(parseMdmTimeString, dateToTime, getDateForTime);

export function isHoursOfOperationValid(hours: IStoreHoursOfOperation, now: Date = new Date()) {
  if (!hours) {
    return false;
  }

  const [openHours, closeHours] = getTodaysHoursOfOperation(hours, now);

  return !!(closeHours && openHours);
}

/*
 * Hours is a strange shape of data. Things like
 * sunOpen: string,
 * sunClose: string,
 * monOpen: string,
 * monClose: string,
 * etc
 */
export function isRestaurantOpen(hours?: IStoreHoursOfOperation | null, now: Date = new Date()) {
  if (!hours || !isHoursOfOperationValid(hours, now)) {
    return false;
  }

  const [openHours, closeHours] = getTodaysHoursOfOperation(hours, now);

  // we send null for opening/closing when we want to close a restaurant
  if (!closeHours || !openHours) {
    return false;
  }

  // if the hours are the same, the store is open 24hrs
  if (openHours === closeHours) {
    return true;
  }

  let close = getDateForHours(closeHours);
  let open = getDateForHours(openHours);

  // If Closing is past midnight, some date manipulation is needed
  // before the times can be compared
  // e.g.
  // open:  06:00
  // close: 01:00
  if (isBefore(close, open)) {
    // Current Time is past midnight
    // e.g.
    // open: 06:00
    // currentTime: 01:00
    if (isBefore(now, open)) {
      const yesterdayDayName = previousDay(now);
      const yesterdayOpenHours = hours[`${yesterdayDayName}Open`];
      if (!yesterdayOpenHours) {
        return false;
      }

      open = getDateForHours(yesterdayOpenHours);
      // Past midnight, the open hours for the previous day are the relevant hours
      // Set Open to the day before for comparison
      open.setDate(open.getDate() - 1);
    } else {
      // Current Time is NOT past midnight
      // e.g.
      // open: 06:00
      // currentTime: 23:00
      const tomorrowDayName = nextDay(now);
      const tomorrowCloseHours = hours[`${tomorrowDayName}Close`];

      if (!tomorrowCloseHours) {
        return false;
      }

      close = getDateForHours(tomorrowCloseHours);
      // Set close to the next day since close will always be before open otherwise
      close.setDate(close.getDate() + 1);
    }
  }
  return isAfter(now, open) && isBefore(now, close);
}
export function checkServiceModeUnavailable(serviceMode: ServiceMode, store: StoreProxy) {
  let serviceModeUnavailable = false;
  switch (serviceMode) {
    case ServiceMode.DELIVERY:
      serviceModeUnavailable = !isRestaurantOpen(store.deliveryHours);
      break;
    case ServiceMode.CURBSIDE:
      serviceModeUnavailable = !isRestaurantOpen(store.curbsideHours);
      break;
    case ServiceMode.DRIVE_THRU:
      serviceModeUnavailable = !isRestaurantOpen(store.driveThruHours);
      break;
    default:
      serviceModeUnavailable = !isRestaurantOpen(store.diningRoomHours);
  }
  return serviceModeUnavailable;
}
export function checkIfWithinOneHourOfCloseToday(
  hours: IStoreHoursOfOperation | null,
  now: Date = new Date()
) {
  if (!hours || !isRestaurantOpen(hours, now)) {
    return false;
  }
  const [, closeHours] = getTodaysHoursOfOperation(hours, now);
  const close = getDateForHours(closeHours);

  return isWithinInterval(now, { start: subHours(close, 1), end: close });
}

// Check given time is within 20 minutes of closing
// and ensuring there is a gap closure of the next opening day
export function isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours(
  hours: IStoreHoursOfOperation,
  now: Date = new Date(),
  minutes = 20
) {
  if (!isRestaurantOpen(hours, now)) {
    return false;
  }
  const [, closeHours] = getTodaysHoursOfOperation(hours, now);
  const close = getDateForHours(closeHours);

  if (!isWithinInterval(now, { start: subMinutes(close, minutes), end: close })) {
    return true;
  }

  // 24 hour store check
  // Subtract one minute due from 00 to 59
  const FOR_24_HOURS_IN_MINUTES_BETWEEN_MONTHS = 60 * 24 - 1;
  const tomorrowDayName = nextDay(now);
  const tomorrowOpenHours = hours[`${tomorrowDayName}Open`];

  const open = getDateForHours(tomorrowOpenHours);

  // The comparison of today close and tmrw open hours
  return differenceInMinutes(close, open) === FOR_24_HOURS_IN_MINUTES_BETWEEN_MONTHS;
}

export function milesBetweenCoordinates(point1: ILocation, point2: Partial<ILocation>): number {
  if (!point1.lat || !point2?.lat) {
    return 0;
  }

  return haversine(point1, point2);
}

const getIsDeliveryAvailable = (store: IStore, deliveryAddress?: IPlaceAddress | null) => {
  const isOrderingAvailable = isMobileOrderingAvailable(store);
  const storeHasPosData = !!store.restaurantPosData;

  const isDeliveryOpen =
    store.hasDelivery && store.deliveryHours && isRestaurantOpen(store.deliveryHours);
  let isSameState = true;
  if (deliveryAddress && store.physicalAddress.stateProvinceShort) {
    isSameState = deliveryAddress.state === store.physicalAddress.stateProvinceShort;
  }
  return isOrderingAvailable && isDeliveryOpen && isSameState && storeHasPosData;
};

export const useDeliveryQuoteSucceeds = () => {
  const { query } = useApolloClient();
  const { logEvent } = useMParticleContext();
  return async (store: IStore, phone: string, deliveryAddress?: IPlaceAddress | null) => {
    const dropoff = {
      addressLine1: deliveryAddress?.addressLine1 ?? '',
      addressLine2: deliveryAddress?.addressLine2,
      city: deliveryAddress?.city ?? '',
      country: deliveryAddress?.country ?? '',
      phoneNumber: phone ?? '',
      state: deliveryAddress?.state ?? '',
      zip: deliveryAddress?.zip ?? ''
    };
    const pickup = {
      addressLine1: store.physicalAddress.address1,
      addressLine2: store.physicalAddress.address2,
      city: store.physicalAddress.city,
      country: store.physicalAddress.country,
      phoneNumber: store.phoneNumber.replace(/-/g, ''),
      state: store.physicalAddress.stateProvince,
      zip: store.physicalAddress.postalCode
    };
    try {
      const { data } = await query<IDeliveryQuoteQuery>({
        query: DeliveryQuoteDocument,
        variables: { dropoff, pickup }
      });

      dataDogLogger({
        message: data.deliveryQuote.status,
        status: StatusType.info
      });
      logEvent(CustomEventNames.FIRST_DELIVERY_QUOTE, EventTypes.Other, {
        status: data.deliveryQuote.status,
        'Restaurant Id': store._id,
        'Restaurant Name': store.name
      });

      return data.deliveryQuote.status;
    } catch (err) {
      logger.error(err);
    }
    return null;
  };
};

export const useGetAvailableRestaurantWithDetails = () => {
  const getRestaurant = useGetRestaurantFn();
  const deliveryQuoteSucceeds = useDeliveryQuoteSucceeds();
  return async (stores: IStore[], phone: string, deliveryAddress?: IPlaceAddress | null) => {
    if (!stores.length) {
      return { storeStatus: StoreStatus.NO_DELIVERY, store: null, storesWithInvalidHOO: [] };
    }

    // If none of the options trigger below, default status to NO_DELIVERY and nearestStore to null
    let nearestStore = null;
    let storeStatus = StoreStatus.NO_DELIVERY;
    let nextEarliestOpen = Infinity;
    let storesWithInvalidHOO = [];
    let deliveryQuote: DeliveryStatus | null = null;

    for (let store of stores) {
      // If store doesn't have delivery, continue
      let isPosOnline = true;

      try {
        /**
         * Disabling lint check because we don't want to query for every store's deliverability.
         * We only want to query if certain conditions are met, and if it returns true, we don't
         * want to query for the rest of the stores
         **/
        // eslint-disable-next-line no-await-in-loop
        const rbiRestaurant = await getRestaurant(store.number);

        isPosOnline = rbiRestaurant.available;

        // operating hours now come from our gql layer
        // so we must merge the results with the sanity store
        store = mergeRestaurantData({ rbiRestaurant, sanityStore: store });
      } catch (error) {
        logger.warn({ error, message: 'Could not fetch restaurant availability', store });
      }

      if (!isPosOnline || !store.hasDelivery || !store.deliveryHours) {
        if (!store.deliveryHours) {
          // Log stores which don't have delivery hours set
          storesWithInvalidHOO.push(store);
        }
        continue;
      }
      // If store with delivery is available, break out of loop and set store
      const isDeliveryAvailable = getIsDeliveryAvailable(store, deliveryAddress);

      if (isDeliveryAvailable) {
        /**
         * Same reason for disabling as above
         */
        // eslint-disable-next-line no-await-in-loop
        deliveryQuote = await deliveryQuoteSucceeds(store, phone, deliveryAddress);

        if (deliveryQuote === DeliveryStatus.QUOTE_SUCCESSFUL) {
          nearestStore = store;
          storeStatus = StoreStatus.OPEN;
          break;
        }
      } else {
        const isHOOValid = isHoursOfOperationValid(store.deliveryHours);
        if (!isHOOValid) {
          // Log stores which don't have delivery hours set
          storesWithInvalidHOO.push(store);
          continue;
        }
      }

      // Otherwise if any store has delivery but is not available, set store status to CLOSED and find earliest open
      storeStatus = StoreStatus.CLOSED;

      const now = new Date();
      const [todayOpenHours] = getTodaysHoursOfOperation(store.deliveryHours);
      const todayOpenDateTime = getDateForHours(todayOpenHours);

      // NOTE: This assumes tomorrow has open hours and could be improved by
      // checking future dates and ensuring we find one with open hours.
      const getTomorrowOpenDateTime = pipe(
        nextOpenDay,
        parseMdmTimeString,
        dateToTime,
        (time: number) => getDateForTime(time, addDays(now, 1))
      );
      // NOTE: This assumes we have proved the store is closed in earlier code
      const isStoreOpenToday = isBefore(now, todayOpenDateTime);

      // if store closed for today get the open time for the next day
      const nextOpenDateTime = isStoreOpenToday
        ? todayOpenDateTime
        : getTomorrowOpenDateTime(store.deliveryHours);

      // If earlier open time found in radius, set new time
      nextEarliestOpen = Math.min(nextOpenDateTime.getTime(), nextEarliestOpen);
    }
    return {
      storeStatus,
      store: nearestStore,
      storesWithInvalidHOO,
      deliveryQuote,
      // If stores are closed, send nextEarliestOpen
      ...(!nearestStore && { nextEarliestOpen: new Date(nextEarliestOpen) })
    };
  };
};

/*
 * isMobileOrderingAvailable compares the app environment with the
 * restaurant environment returned from Sanity
 */
export function isMobileOrderingAvailable(restaurant: StoreProxy): boolean {
  const { mobileOrderingStatus } = restaurant;

  if (!restaurant?.hasMobileOrdering || !mobileOrderingStatus) {
    return false;
  }

  return validMobileOrderingEnvs().includes(mobileOrderingStatus);
}
